import { useState } from 'react';
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getLocalStorageItem } from '../utils/localStorageDomain';
import { domainMap } from '../utils/domainMap';

const determineDomain = (): string => {
  let domain = getLocalStorageItem('domain');

  if (!domain) {
    const url = window.location.hostname;
    domain = Object.keys(domainMap).find((key) => url.includes(key)) || 'staging';
  }

  return domain.replace(/['"]+/g, '');
};

// Cache para evitar requisições duplicadas
const requestCache = new Map<string, { data: any; timestamp: number }>();

// Duração de cache por tipo de rota (em milissegundos)
const CACHE_DURATIONS: Record<string, number> = {
  // Rotas que raramente mudam - cache de 5 minutos
  'annual-fees/latest': 300000, // 5 minutos
  'companies/info': 180000, // 3 minutos
  'documents': 120000, // 2 minutos
  'notifications': 60000, // 1 minuto
  // Cache padrão para outras rotas
  'default': 60000 // 1 minuto
};

// Função para determinar a duração do cache baseado na URL
const getCacheDuration = (url: string): number => {
  for (const [key, duration] of Object.entries(CACHE_DURATIONS)) {
    if (key !== 'default' && url.includes(key)) {
      return duration;
    }
  }
  return CACHE_DURATIONS.default;
};

// Request deduplication - evita múltiplas requisições simultâneas idênticas
const pendingRequests = new Map<string, Promise<any>>();

// Contador de retries para backoff exponencial
const retryCounts = new Map<string, number>();

// Gera uma chave única para a requisição
// Para GET requests, ignora headers (exceto se necessário para autenticação)
// Isso permite que requisições com headers diferentes compartilhem cache
const getRequestKey = (config: InternalAxiosRequestConfig): string => {
  const method = (config.method || 'get').toUpperCase();
  const url = config.url || '';
  
  // Normaliza a URL removendo query strings duplicadas ou ordenação diferente
  const normalizedUrl = url.split('?')[0] + (config.params ? `?${JSON.stringify(config.params)}` : '');
  
  // Para GET requests, não inclui headers na chave (a resposta é a mesma)
  // Isso permite cache compartilhado mesmo com headers diferentes (Content-Language, etc)
  return `${method}_${normalizedUrl}`;
};

// Limpa cache antigo periodicamente
if (typeof window !== 'undefined') {
  setInterval(() => {
    const now = Date.now();
    requestCache.forEach((value, key) => {
      // Extrai a URL da chave (formato: METHOD_URL_PARAMS)
      // A URL está entre o primeiro e segundo underscore
      const parts = key.split('_');
      const url = parts.length > 1 ? parts.slice(1, -1).join('_') : '';
      const cacheDuration = getCacheDuration(url);
      
      if (now - value.timestamp > cacheDuration) {
        requestCache.delete(key);
      }
    });
  }, 60000); // Limpa a cada 60 segundos
}

// Função para criar instância de API (usada pelo Redux store)
export const createApiInstance = (): AxiosInstance => {
  const domain = determineDomain();
  const baseURL = domainMap[domain] || domainMap['staging'];

  const api = axios.create({
    baseURL: baseURL,
    headers: {
      Accept: 'application/json'
    }
  });

  // Aplica o mesmo wrapper de cache e deduplication
  return applyCacheAndDeduplication(api);
};

// Aplica cache e deduplication a uma instância de API
const applyCacheAndDeduplication = (api: AxiosInstance): AxiosInstance => {
  // Wrapper para request deduplication e cache
  const originalRequest = api.request.bind(api);
  api.request = function(config: any): Promise<any> {
    const method = (config.method || 'get').toLowerCase();
    const isReadOnly = method === 'get';
    
    // Se for um retry (429), pula cache/deduplication para tentar novamente
    if ((config as any).__isRetry) {
      delete (config as any).__isRetry;
      return originalRequest(config);
    }
    
    if (!isReadOnly) {
      // Para métodos que modificam dados, não usa cache/deduplication
      return originalRequest(config);
    }

    const requestKey = getRequestKey(config);
    const cacheDuration = getCacheDuration(config.url || '');
    
    // Verifica cache primeiro
    const cached = requestCache.get(requestKey);
    if (cached && Date.now() - cached.timestamp < cacheDuration) {
      const age = Math.floor((Date.now() - cached.timestamp) / 1000);
      console.log(`[API Cache] ✅ Hit (${age}s): ${config.url?.substring(0, 80)}...`);
      // Usa setTimeout para evitar problemas com Suspense durante atualizações síncronas
      return new Promise((resolve) => {
        // Usa queueMicrotask para garantir que seja assíncrono mas sem delay visível
        queueMicrotask(() => {
          resolve({
            data: cached.data,
            status: 200,
            statusText: 'OK',
            headers: {},
            config: config
          });
        });
      });
    }
    
    // Se o cache expirou, remove da memória
    if (cached && Date.now() - cached.timestamp >= cacheDuration) {
      requestCache.delete(requestKey);
    }
    
    // Verifica se já há uma requisição pendente idêntica (ANTES de criar nova)
    // Isso evita múltiplas requisições simultâneas para a mesma rota
    let existingPromise = pendingRequests.get(requestKey);
    if (existingPromise) {
      console.log(`[API Dedup] ✅ Reutilizando requisição pendente: ${config.url?.substring(0, 80)}...`);
      return existingPromise;
    }
    
    // Cria uma promise placeholder ANTES de fazer a requisição real
    // Isso garante que outras requisições simultâneas vejam esta promise imediatamente
    let resolvePending: ((value: any) => void) | null = null;
    let rejectPending: ((error: any) => void) | null = null;
    
    const pendingPromise = new Promise((resolve, reject) => {
      resolvePending = resolve;
      rejectPending = reject;
    });
    
    // Adiciona o placeholder à lista de pendentes IMEDIATAMENTE
    // Isso é crítico para evitar race conditions
    pendingRequests.set(requestKey, pendingPromise);
    
    // Agora faz a requisição real
    const requestPromise = originalRequest(config)
      .then((response) => {
        // Cache apenas GET requests bem-sucedidas
        if (response.status === 200) {
          requestCache.set(requestKey, {
            data: response.data,
            timestamp: Date.now()
          });
          console.log(`[API Cache] 💾 Armazenado: ${config.url?.substring(0, 80)}...`);
        }
        pendingRequests.delete(requestKey);
        
        // Resolve o placeholder promise com a resposta
        if (resolvePending) {
          resolvePending(response);
        }
        
        return response;
      })
      .catch((error) => {
        pendingRequests.delete(requestKey);
        
        // Rejeita o placeholder promise com o erro
        if (rejectPending) {
          rejectPending(error);
        }
        
        throw error;
      });
    
    // Verifica novamente se outra requisição foi adicionada enquanto criávamos esta
    // (proteção adicional contra race conditions)
    existingPromise = pendingRequests.get(requestKey);
    if (existingPromise && existingPromise !== pendingPromise) {
      // Outra requisição foi criada primeiro, remove esta e usa a existente
      console.log(`[API Dedup] ⚠️ Race condition detectado, usando requisição existente: ${config.url?.substring(0, 80)}...`);
      pendingRequests.delete(requestKey);
      return existingPromise;
    }
    
    // Retorna o placeholder promise que será resolvido quando a requisição completar
    return pendingPromise;
  };

  // Interceptor de resposta - retry com backoff exponencial para 429
  api.interceptors.response.use(
    (response) => {
      const requestKey = getRequestKey(response.config);
      // Limpa contador de retry em caso de sucesso
      retryCounts.delete(requestKey);
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      
      if (!originalRequest) {
        return Promise.reject(error);
      }

      const requestKey = getRequestKey(originalRequest);
      
      // Retry simples para 429 - tenta infinitamente a cada 3 segundos
      if (error.response?.status === 429) {
        const currentRetryCount = retryCounts.get(requestKey) || 0;
        const delay = 3000; // 3 segundos entre cada tentativa
        
        // Incrementa o contador de retries
        retryCounts.set(requestKey, currentRetryCount + 1);
        
        console.log(`[API] Rate limit (429) - Aguardando ${delay}ms antes do retry ${currentRetryCount + 1}...`);
        
        // Aguarda 3 segundos antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, delay));
        
        console.log(`[API] Executando retry ${currentRetryCount + 1}...`);
        
        // Marca como retry para pular cache/deduplication
        (originalRequest as any).__isRetry = true;
        
        // Retry da requisição - usa api.request() que passa pelos interceptors novamente
        // Se ainda der 429, o interceptor será chamado novamente e tentará mais uma vez
        return api.request(originalRequest);
      }
      
      // Limpa contador de retry para outros tipos de erro
      retryCounts.delete(requestKey);
      
      return Promise.reject(error);
    }
  );

  return api;
};

export const useApi = (): AxiosInstance => {
  const [api] = useState<AxiosInstance>(() => {
    return createApiInstance();
  });

  return api;
};
